import { useOrgStore } from '@/stores/org-store';
import { apiRequest } from '@/utils/api-request';

interface TestGuardrailsConfigProps {
  userQuery: string;
}

/**
 * Represents a single guardrail check result
 */
interface GuardrailCheckResult {
  name: string;
  reason: string;
  success: boolean;
}

/**
 * Response from the guardrails check API
 * - Empty array ([]) indicates all checks passed
 * - Array with items indicates failed checks with details
 */
type GuardrailsCheckResponse = GuardrailCheckResult[];

interface TestGuardrailsResponse {
  result: 'pass' | 'fail';
  detail?: string;
  failures?: GuardrailCheckResult[];
}

export const testGuardrailsConfig = async ({
  userQuery,
}: TestGuardrailsConfigProps): Promise<TestGuardrailsResponse> => {
  const orgId = useOrgStore.getState().currentOrg?.orgid;
  const response = await apiRequest<GuardrailsCheckResponse>({
    url: `/guardrails/${orgId}/check/prompt`,
    method: 'POST',
    target: 'platform',
    body: {
      user_query: userQuery,
    },
  });

  if (response?.detail) {
    return {
      result: 'fail',
      detail: response.detail || 'Failed to test guardrails config',
    };
  }

  // The response data should be an array of guardrail check results
  if (response.data && Array.isArray(response.data)) {
    // Empty array means all checks passed
    if (response.data.length === 0) {
      return {
        result: 'pass',
      };
    }
    // Non-empty array means some checks failed
    return {
      result: 'fail',
      failures: response.data,
    };
  }

  // Fallback for unexpected response format
  return {
    result: 'fail',
    detail: 'Unexpected response format from guardrails check',
  };
};
